<template>
  <div>
    <div>
      <v-row>

        <!-- Sort By menu -->
        <v-col cols="2" sm="1" align-self="center">
          <v-menu
              v-model="sortMenu"
              offset-x
              :close-on-content-click="false"
              min-width="400"
          >
            <template v-slot:activator="{ on: menu}">
              <v-tooltip bottom>
                <template v-slot:activator="{ on:tooltip}">
                  <v-btn icon v-on="{...menu, ...tooltip}">
                    <v-icon>sort</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('components:common.sort-by') }}</span>
              </v-tooltip>
            </template>

            <v-card class="pa-5">
              <h6 class="greyDark--text mb-0 font-weight-bold">{{ $t('components:common.sort-by') }}</h6>

              <v-row no-gutters justify="space-between">
                <v-col cols="5">
                  <v-radio-group v-model="sortBy">
                    <v-radio
                        v-for="(sort, i) in filteredSorts"
                        :key="i"
                        :label="sort.text"
                        :value="sort.value"></v-radio>
                  </v-radio-group>
                </v-col>

                <v-col cols="5">
                  <v-radio-group v-model="sortDesc">
                    <v-radio color="secondary" :label="$t('components:common.descending')" :value="false"></v-radio>
                    <v-radio color="secondary" :label="$t('components:common.ascending')" :value="true"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-spacer></v-spacer>
                <v-btn
                    text
                    @click="sortMenu = false"
                >
                  {{ $t('buttons:close') }}
                </v-btn>
              </v-row>
            </v-card>
          </v-menu>
        </v-col>

        <!-- Items per page select -->
        <v-col
            cols="12"
            sm="11"
            md="3">
          <v-select
              :menu-props="{ bottom: true, offsetY: true }"
              v-model="options.itemsPerPage"
              :items="elementsPerPage"
              hide-details
              :label="$t('components:common.elements-per-page')"
          />
        </v-col>

        <!-- Filter -->
         <v-col
             cols="12"
             sm="6"
             :md="disableFilter ? 3 : 4"
             lg="4"
             v-if="!disableFilter">
           <v-select
               :items="filters"
               :item-value="filterValueField"
               :item-text="filterTextField"
               :menu-props="{ bottom: true, offsetY: true }"
               v-model="filterBy"
               hide-details
               clearable
               :label="$t('components:common.filter-by')"
           />
        </v-col>
        <v-col class="d-none d-md-block" v-if="disableFilter"></v-col>

        <!-- Search -->
        <v-col
               cols="12"
               :sm="disableFilter ? 6 : 6"
               :md="disableFilter ? 5 : 4"
               lg="4">
          <v-text-field
              v-if="!disableSearch"
              v-model="search"
              append-icon="search"
              :value="computedSearch"
              :label="$t('components:common.search')"
              single-line
              hide-details
              clearable
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchAndSortBar',
  props: {
    cachedSearch: String, // chached Search String from prev Search
    disableSearch: Boolean, // Boolean to disable and hide search

    defaultDesc: Boolean,
    defaultSortBy: String,
    defaultFilterBy: String,

    sorts: Array,
    filters: Array,
    filterValueField: String,
    filterTextField: String,

    disableFilter: Boolean
  },
  data() {
    return {
      search: '',
      options: {
        sortBy: ['name'],
        sortDesc: [false],
        itemsPerPage: 10,
        page: 1
      },
      elementsPerPage: [
        {text: '5', value: 5},
        {text: '10', value: 10},
        {text: '15', value: 15},
        {text: this.$t('common:all'), value: -1}
      ],
      sortDesc: false,
      sortBy: null,
      filteredSorts: [],

      searchTimer: null,

      filterBy: null,
      sortMenu: false
    }
  },
  watch: {
    search() {
      this.computedSearch = this.search
      //use timer function to wait for user to finish until we search
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.options.page = 1; //reset the page back to 1 on search, otherwise we can land on an empty page
        this.callDashboardUpdate();
      }, 500);
    },
    options: {
      handler() {
        this.callDashboardUpdate();
      },
      deep: true,
      immediate: true //with this it will trigger on initialization -> we don't have to load on mount

    },
    sortDesc() {
      this.options.sortDesc[0] = this.sortDesc;
      this.options.page = 1;
      this.callDashboardUpdate();
    },
    sortBy() {
      this.options.sortBy[0] = this.sortBy;
      this.options.page = 1;
      this.callDashboardUpdate();
    },
    filterBy() {
      this.options.page = 1;
      this.callDashboardUpdate();
    },
    cachedSearch() {
      if (this.cachedSearch) {
        this.search = this.cachedSearch;
      }
    },
    'options.itemsPerPage'(newVal, oldVal) {
      if (newVal != oldVal) {
        this.options.page = 1;
      }
    },

    sorts() {
      if (this.sorts) {
        let forbiddenFields = ["controls", "checkbox", "checkbox2", "checkbox3"]
        let resultSort = [];
        for (let i = this.sorts.length - 1; i >= 0; i--) {
          if (!forbiddenFields.includes(this.sorts[i].value)) {
            resultSort.push(this.sorts[i]);
          }
        }
        this.filteredSorts = resultSort;
      }
    },
  },
  computed: {
    computedSearch: {
      get() {
        if (this.search) {
          this.$emit('cacheCommit', this.search);
          return this.search
        }
        this.$emit('cacheCommit', null);
        return null
      },
      set(value) {
        this.search = value;
      }
    },
  },
  mounted() {
    if (this.cachedSearch) {
      this.search = this.cachedSearch;
    }
    if (this.defaultDesc) {
      this.sortDesc = this.defaultDesc;
    }
    if (this.defaultSortBy) {
      this.sortBy = this.defaultSortBy;
    }

    // filter out specific sort fields
    if (this.sorts) {
      let forbiddenFields = ["controls", "checkbox", "checkbox2", "checkbox3"]
      for (let i = this.sorts.length - 1; i >= 0; i--) {
        if (!forbiddenFields.includes(this.sorts[i].value)) {
          this.filteredSorts.push(this.sorts[i]);
        }
      }
    }
  },
  methods: {
    callDashboardUpdate() {
      clearTimeout(this.tableUpdateTimer);
      this.tableUpdateTimer = setTimeout(() => {
        this.$emit('updateData', this.options, this.search, this.filterBy);
      }, 50);
    },
  }
}
</script>
